import React from 'react';
import { Link } from 'react-router-dom';
import BoutonAide from './BoutonAide';
import { fondTeinte, texteSurClair } from '../../theme/charte';

/* Éléments communs à tous les écrans du portail, d'après les maquettes :
   en-tête de page (surtitre en capitales, titre Fraunces, phrase, action à
   droite), titre de section, message, pastille de statut, états. */

export const EnTetePage: React.FC<{
  titre: React.ReactNode;
  surtitre?: React.ReactNode;
  sousTitre?: React.ReactNode;
  aide?: string;
  actions?: React.ReactNode;
}> = ({ titre, surtitre, sousTitre, aide, actions }) => (
  <header className="entete-page">
    <div className="entete-page__textes">
      {surtitre && <div className="surtitre">{surtitre}</div>}
      <div className="entete-page__titre">
        <h1>{titre}</h1>
        {aide && <BoutonAide texte={aide} />}
      </div>
      {sousTitre && <p className="entete-page__sous-titre">{sousTitre}</p>}
    </div>
    {actions && <div className="ligne">{actions}</div>}
  </header>
);

export const TitreSection: React.FC<{
  titre: React.ReactNode;
  aide?: string;
  niveau?: 2 | 3;
  id?: string;
  compteur?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ titre, aide, niveau = 2, id, compteur, action }) => {
  const Balise = niveau === 2 ? 'h2' : 'h3';
  return (
    <div className={`titre-section${action ? ' titre-section--entre' : ''}`}>
      <div className="titre-section" style={{ alignItems: 'center' }}>
        <Balise id={id} style={niveau === 3 ? undefined : { fontSize: '22px' }}>{titre}</Balise>
        {compteur != null && <span className="compteur-section">{compteur}</span>}
        {aide && <BoutonAide texte={aide} />}
      </div>
      {action}
    </div>
  );
};

/** Icône « plus » du bouton Nouvelle mission (maquettes). */
export const IconePlus: React.FC = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 5v14M5 12h14" /></svg>
);

/** Icône de téléchargement (bouton PDF des listes de documents). */
export const IconeTelecharger: React.FC = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v11M7 11l5 5 5-5M5 20h14" /></svg>
);

/** Icône d'information (barres d'information, alertes). */
export const IconeInfo: React.FC<{ couleur?: string }> = ({ couleur = 'currentColor' }) => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={couleur} strokeWidth="2" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
);

export type GenreMessage = 'erreur' | 'succes' | 'attention' | 'info';

export const Message: React.FC<{
  genre: GenreMessage;
  children: React.ReactNode;
  onFermer?: () => void;
}> = ({ genre, children, onFermer }) => (
  <div className={`message message--${genre}`} role={genre === 'erreur' ? 'alert' : 'status'}>
    <div className="message__texte">{children}</div>
    {onFermer && (
      <button type="button" className="message__fermer" onClick={onFermer} aria-label="Fermer le message">×</button>
    )}
  </div>
);

/** Pastille de statut : point et texte dans la couleur du statut, fond teinté. */
export const Badge: React.FC<{ libelle: string; couleur: string }> = ({ libelle, couleur }) => (
  <span className="badge" style={{ color: texteSurClair(couleur), background: fondTeinte(couleur), '--point': couleur } as React.CSSProperties}>{libelle}</span>
);

export const Chargement: React.FC<{ texte?: string }> = ({ texte = 'Chargement…' }) => (
  <div className="etat" role="status">
    <span className="roue" aria-hidden />
    <span>{texte}</span>
  </div>
);

export const EtatVide: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="etat">{children}</div>
);

/** Lien de retour en tête de page (maquette Mission : chevron et libellé). */
export const LienRetour: React.FC<{ vers: string; children: React.ReactNode }> = ({ vers, children }) => (
  <Link to={vers} className="lien-retour">
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M15 5l-7 7 7 7" /></svg>
    {children}
  </Link>
);
